/**
 * Tiny event bus between the product and the guide.
 *
 * The product says what happened ("draft.sent", "calendar.connected"); a
 * chapter waiting on a task resolves. Neither side imports the other, so the
 * inbox doesn't need to know a guide exists, and a signal with no listener
 * costs nothing.
 */

export type GuideSignal =
    | 'draft.sent'
    | 'draft.edited'
    | 'thread.opened'
    | 'calendar.connected'
    | 'pitch.settings_saved'
    | 'mediakit.shared'
    | 'invoice.created'
    | 'task.completed'
    | 'agreement.redlines_requested'
    | 'agreement.signed'
    | 'agent.message_sent'
    /** Home: the assistant finished answering a request (detail: `{ conversationId }`). */
    | 'agent.reply_done'
    /** Pitching: the composer wrote pitch drafts (detail: `{ pitchIds }`). */
    | 'pitch.drafted'
    /** Pitching: a pitch went out (detail: `{ pitchId }`). */
    | 'pitch.sent'
    /** Pitching: creators were added to the watchlist (detail: `{ count }`). */
    | 'watchlist.added'
    /** Pitching: the pitch template was saved in the template editor. */
    | 'pitch.template_saved'
    /** Insights: the creator picked another period (detail: `{ period }`). */
    | 'insights.period_changed'

type Listener = (detail?: Record<string, unknown>) => void

const listeners = new Map<string, Set<Listener>>()

export function emitGuideSignal(name: GuideSignal, detail?: Record<string, unknown>): void {
    listeners.get(name)?.forEach((fn) => {
        try {
            fn(detail)
        } catch {
            // A listener's failure must never break the product action that emitted it.
        }
    })
}

export function onGuideSignal(name: string, fn: Listener): () => void {
    let set = listeners.get(name)
    if (!set) {
        set = new Set()
        listeners.set(name, set)
    }
    set.add(fn)
    return () => {
        set?.delete(fn)
    }
}

/** Is anything listening? Lets the product skip work that only matters to a waiting chapter. */
export function isGuideWaitingFor(name: GuideSignal): boolean {
    return (listeners.get(name)?.size ?? 0) > 0
}
